import { forEach } from './array-utils';
import { allowBodyScroll } from './body-scroll';
import { isMobile } from './device-utils';

const config = require('config');

export const IS_HIDDEN_CLASSNAME = 'is-hidden';

/**
 *
 * @param {HTMLElement} object
 */
export const isDOMElement = (object) => {
  return typeof HTMLElement === 'object'
    ? object instanceof HTMLElement // DOM2
    : object &&
        typeof object === 'object' &&
        object !== null &&
        object.nodeType === 1 &&
        typeof object.nodeName === 'string';
};

/**
 * Remove element from dom
 */

export const removeElementDom = (element) => {
  if (isDOMElement(element)) {
    element.parentNode.removeChild(element);
  } else {
    console.error('the element you want to delete is not a DOM object');
  }
};

/**
 * Clear everything inside a DOM element
 * @param {*} $node
 */
export const clearNode = ($node) => {
  if (!isDOMElement($node)) {
    console.error('the node you want to clear is not a DOM object');
    return;
  }
  $node.textContent = '';
};

/**
 * Remove the no-js class on the <html> element if JavaScript is on.
 * By default place a no-js class on the <html> element: <html class="no-js">
 */
export const removeNoJsClassFromHTMLElement = () => {
  document.documentElement.classList.remove(config.noJs.className || 'no-js');
};

/**
 * Insert a new node after a reference node.
 * @param {Node} referenceNode
 * @param {Node} newNode
 */
export const insertAfter = (referenceNode, newNode) => {
  if (referenceNode && newNode) {
    referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
  }
};

/**
 * Get Array of dataset model by Node
 *
 * @param {*} classname classname
 */
export const getDatasetArrayByNode = (node) => {
  return JSON.parse(JSON.stringify(node.dataset));
};

/**
 * It removes all possible previous listeners for a specific node
 * Take care as it also clears event listeners on all children
 * @param {*} node node to clean
 */
export const removeAllListeners = (node) => {
  const nodeCloned = node.cloneNode(true); // Create a new node without any listener attached
  node.parentNode.replaceChild(nodeCloned, node); // Update the DOM
  return nodeCloned;
};

export const getCoords = (elem) => {
  // crossbrowser version
  const box = elem.getBoundingClientRect();
  const body = document.body;
  const docEl = document.documentElement;
  const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
  const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;
  const clientTop = docEl.clientTop || body.clientTop || 0;
  const clientLeft = docEl.clientLeft || body.clientLeft || 0;
  const top = box.top + scrollTop - clientTop;
  const left = box.left + scrollLeft - clientLeft;
  const bottom = box.bottom + scrollTop - clientTop;
  return { top: Math.round(top), left: Math.round(left), bottom: Math.round(bottom) };
};

export const getHeight = (element) => {
  if (!element) {
    return 0;
  }
  return parseFloat(window.getComputedStyle(element).height, 10);
};

/**
 * Utility function to create DOM element with populated attributes and content
 *
 * Usage :
 *
 * // simple element
 * let input = createDomElement("input", {
 *   type: "search",
 *   placeholder: "Enter what pleases you"
 * });
 *
 * // simple element with HTML content
 * let label = createDomElement("label", {},
 *   "<span>Hit <kbd>Esc</kbd> to close</span>"
 * );
 *
 * @param {String} tagName
 * @param {Object} attributes
 * @param {String} html
 * @returns {Element}
 */
export const createDomElement = (tagName, attributes = {}, html = null) => {
  const element = document.createElement(tagName);

  for (const [attributeKey, attributeValue] of Object.entries(attributes)) {
    element.setAttribute(attributeKey, attributeValue);
  }

  if (html) {
    element.innerHTML = html;
  }

  return element;
};

/**
 * Add a CSS class to each node in a DOMNodeList depending on the possible data attribute to check
 * @param {NodeList} nodes a list of nodes
 * @param {String} className
 * @param {Object} dataAttributeToCheck for each node it determines whether to add the className or not
 * Example: dataAttributeToCheck = { name: 'state', value: 'sticky' }
 */
export const addClassToNodes = ({ nodes = [], className, dataAttributeToCheck }) => {
  forEach((node) => {
    if (
      !dataAttributeToCheck ||
      node.getAttribute(`data-${dataAttributeToCheck.name}`) === dataAttributeToCheck.value
    ) {
      node.classList.add(className);
    }
  })(nodes);
};

/**
 * Remove a CSS class from each node in a DOMNodeList
 * @param {NodeList} nodes a list of nodes
 * @param {String} className
 */
export const removeClassFromNodes = (nodes = [], className) => {
  // Node list
  forEach((player) => {
    player.classList.remove(className);
  })(nodes);
};

/**
 * Smooth scroll to the top/bottom of an element with an optional responsive offset
 * @param {Object} { $element, top = true, responsiveOffset, delay }
 */
export const scrollToElement = ({ $element, top = true, responsiveOffset = { mobile: 0, desktop: 0 }, delay = 0 }) => {
  if (!$element) {
    return;
  }

  setTimeout(() => {
    const yOffset = isMobile() ? -responsiveOffset.mobile : -responsiveOffset.desktop;
    const elementBoundingClientRect = top
      ? $element.getBoundingClientRect().top
      : $element.getBoundingClientRect().bottom;
    const y = elementBoundingClientRect + window.pageYOffset + yOffset;

    window.scrollTo({ top: y, behavior: 'smooth' });
  }, delay);
};

/**
 * Smooth scroll to the top of the page
 */
export const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

/**
 * When an HTML node change for some reasons --> trigger a callback
 * @param {HTML Node} $targetNode
 * @param {Function} callback
 * @param {Object} mutationsConfig
 */
export const observeNodeMutation = ({ $targetNode, callback, mutationsConfig }) => {
  if (!isDOMElement($targetNode)) {
    return;
  }
  const mutationObserver = new MutationObserver(callback);
  mutationObserver.observe($targetNode, mutationsConfig);
};

/**
 * When an HTML node change its size --> trigger a callback
 * @param {HTML Node} $targetNode
 * @param {Function} callback
 */
export const observeNodeResize = ($targetNode, callback) => {
  if (!isDOMElement($targetNode)) {
    return;
  }
  const resizeObserver = new ResizeObserver((entries) => {
    callback(entries, resizeObserver);
  });
  resizeObserver.observe($targetNode);
};

export const isNodeInView = ($targetNode) => {
  if (!isDOMElement($targetNode)) {
    return;
  }
  const rect = $targetNode.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    Math.floor(rect.bottom) <= (window.innerHeight || document.documentElement.clientHeight) &&
    Math.floor(rect.right) <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

export const isNodeOnFocus = ($targetNode, topThreshold = 0) => {
  if (!isDOMElement($targetNode)) {
    return;
  }
  return (
    $targetNode.getBoundingClientRect().top < topThreshold && $targetNode.getBoundingClientRect().bottom > topThreshold
  );
};

export const hideElement = (element) => {
  if (isDOMElement(element)) {
    element.classList.add(IS_HIDDEN_CLASSNAME);
  } else {
    console.error('the element you want to hide is not a DOM object');
  }
};

export const unHideElement = (element) => {
  if (isDOMElement(element)) {
    element.classList.remove(IS_HIDDEN_CLASSNAME);
  }
};

export const isHiddenElement = (element) => {
  if (isDOMElement(element)) {
    return element.classList.contains(IS_HIDDEN_CLASSNAME);
  }
  console.error('the element you want to check is not a DOM object');
};

export const closeBurgerMenu = () => {
  const $localNav = document.querySelector('.j-local-nav') || null;
  const $localNavOpen = document.querySelector('.j-local-nav__trigger .icon__svg--menu').parentNode || null;
  const $localNavClose = document.querySelector('.j-local-nav__trigger .icon__svg--close').parentNode || null;
  const elementFeatured = document.querySelector('.j-local-nav__featured') || null;
  const elementBottom = document.querySelector('.j-local-nav__bottom') || null;
  const elementSecondary = document.querySelector('.j-local-nav__secondary') || null;
  allowBodyScroll();
  $localNav.classList.remove('is-active__wrapper');
  $localNavOpen.classList.add('icon__svg--show');
  $localNavOpen.classList.remove('icon__svg--hide');
  $localNavClose.classList.add('icon__svg--hide');
  $localNavClose.classList.remove('icon__svg--show');
  if (elementFeatured) {
    elementFeatured.classList.remove('is-active--mob');
    elementFeatured.classList.remove('is-active');
  }
  if (elementBottom) {
    elementBottom.classList.remove('is-active');
  }
  if (elementSecondary) {
    elementSecondary.classList.remove('is-active');
  }
};

export const isParagraph = (element) => element?.tagName === 'P';
export const isAnchor = (element) => element?.tagName === 'A';
export const hasClassName = (element, className) => element?.className.includes(className);

export const injectScriptOnHead = ($script, src) => {
  if (document.head.querySelector(`script[src="${src}"]`)) {
    return;
  }
  document.head.appendChild($script);
};

export const isVerticallyOverflowing = ($element) => {
  if (!$element) {
    return;
  }
  return $element.scrollHeight > $element.clientHeight;
};
